import { capitalize, round } from 'lodash'
import { MouseEventHandler } from 'react'

import { SuggestedTradeView } from 'enums/SuggestedTradeView'
import { PortfolioSuggestedTradeFull, TRADE_ACTION } from 'types'

import PDSAssetTag from 'designSystem/components/PDSAssetTag/PDSAssetTag'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSIcon from 'designSystem/components/PDSIcon/PDSIcon'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSValueTag from 'designSystem/components/PDSValueTag/PDSValueTag'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { formatAsCurrency } from 'utils'
import { roundToHundredths } from 'utils/numberUtils'

type Props = {
  onAssetClick: MouseEventHandler
  trade: PortfolioSuggestedTradeFull
  tradeView: SuggestedTradeView
}

export default function PortfolioSuggestedTradesListItem({
  onAssetClick,
  trade,
  tradeView,
}: Props) {
  const {
    action,
    assetName,
    currentWeight = 0,
    realizedGain = 0,
    recommendedWeight = 0,
    shares,
    symbol,
    value = 0,
  } = trade

  const currentWeightPercentage = round(currentWeight * 100, 2)
  const recommendedWeightPercentage = round(recommendedWeight * 100, 2)

  const absoluteRelativeGain = formatAsCurrency(Math.abs(realizedGain))
  let realizedGainLabel = 'No Gain'
  if (realizedGain < 0) {
    realizedGainLabel = `${absoluteRelativeGain} Loss`
  }
  if (realizedGain > 0) {
    realizedGainLabel = `${absoluteRelativeGain} Gain`
  }

  return (
    <PDSFlexBox
      border={`1.6px solid ${COLOR.content_border}`}
      borderRadius={SIZE.s8}
      className="interactive-card"
      justifyContent="space-between"
      onClick={onAssetClick}
      padding={SIZE.s12}
      style={{ cursor: 'pointer' }}
      testId={`trade-${symbol?.toLowerCase()}`}
    >
      <PDSFlexBox alignItems="center" gap={SIZE.s8} width="64%">
        <PDSFlexBox width="90%">
          <PDSAssetTag marginRight={SIZE.s8} symbol={symbol} />
          <PDSText hideOverflow={true} weight="medium">
            {assetName}
          </PDSText>
        </PDSFlexBox>

        <PDSBox width="2.5rem" marginLeft={SIZE.s8}>
          {/* Tooltip for mapped assets here */}
        </PDSBox>
      </PDSFlexBox>
      <PDSFlexBox alignItems="center" gap={SIZE.s12}>
        {tradeView === SuggestedTradeView.PERCENTAGE && (
          <>
            <PDSValueTag
              decimals={2}
              direction="none"
              minWidth="4.5rem"
              showDirection={false}
              testId={`trade-${symbol?.toLowerCase()}-weight-current`}
              variant="arrow"
              value={currentWeightPercentage}
            />
            <PDSIcon
              name="arrowRight"
              size="md"
              testId={`trade-${symbol?.toLowerCase()}-${action.toLowerCase()}`}
            />
            <PDSValueTag
              decimals={2}
              direction={action === TRADE_ACTION.BUY ? 'up' : 'down'}
              showDirection={false}
              minWidth="4.5rem"
              testId={`trade-${symbol?.toLowerCase()}-weight-optimized`}
              variant="arrow"
              value={recommendedWeightPercentage}
            />
          </>
        )}
        {tradeView === SuggestedTradeView.SHARE && (
          <PDSText testId={`trade-${symbol?.toLowerCase()}-shares`}>
            {`${capitalize(action)} ${roundToHundredths(shares)} shares`}
          </PDSText>
        )}
        {tradeView === SuggestedTradeView.VALUE && (
          <PDSText testId={`trade-${symbol?.toLowerCase()}-value`}>
            {`${capitalize(action)} ${formatAsCurrency(value)}`}
          </PDSText>
        )}
        {tradeView === SuggestedTradeView.GAIN && (
          <PDSText testId={`trade-${symbol?.toLowerCase()}-gain`}>
            {realizedGainLabel}
          </PDSText>
        )}
      </PDSFlexBox>
    </PDSFlexBox>
  )
}
